import get from 'get-value'
import { getAPIInternalBaseURL } from '../../../NonReact/constants/ApiConstants'
import {
    BroadworksSSOConfigDTO,
    FeaturesConfigDTO,
    MicrosoftSSOConfigDTO,
    MiscAppConfigDTO,
    TelSysModalConfigDTO,
    VersionsConfigDTO
} from '../../../NonReact/Types/Types'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

const FEATURES_FILE_NAME: string = 'features'
const VERSIONS_FILE_NAME: string = 'versions'
const BROADWORKS_SSO_FILE_NAME: string = 'broadworks-sso-config'
const MICROSOFT_SSO_FILE_NAME: string = 'microsoft-sso-config'
const MISC_APP_CONFIG_FILE_NAME: string = 'miscAppConfig'
const TELSYS_MODAL_CONFNIG_NAME: string = 'telsys-modal-config'
const NEW_UI_FILE_NAME: string = 'new-ui'
const CME_DISABLED_CAPABILITIES_FILE_NAME: string = 'cme-disabledCapabilities-config'

/**
 * Configuration for linking legacy Akixi users into the new Fusion UI.
 */
export type NewUIConfigDTO = {
    url?: string
}

/**
 * CME capabilities that are not yet available, from the `cme-disabledCapabilities-config` generic config.
 * `TelSysType` maps a TelSys type key ('BroadWorks', 'Webex', 'MSTeams') to the capability keys
 * ('CXA', 'CXI', 'CXS', 'CXR') unavailable for that type; `Common.aiAssist: true` makes AI Assist unavailable.
 */
export type CMEDisabledCapabilitiesConfigDTO = {
    disabledCapabilities?: {
        TelSysType?: Partial<Record<string, string[]>>
        Common?: { aiAssist?: boolean }
    }
}

export const getConfig = <T extends unknown>(config: string): Promise<JSONResponse<T>> =>
    requestGetJSON(getAPIInternalBaseURL().appendPathParams('genericConfig', config), {
        cacheTime: 500000
    })

export const getFeaturesConfig = (): Promise<JSONResponse<FeaturesConfigDTO>> =>
    getConfig<FeaturesConfigDTO>(FEATURES_FILE_NAME)

export const getFeaturesConfigValue = (key: string): Promise<boolean> =>
    getFeaturesConfig().then(
        (response: JSONResponse<FeaturesConfigDTO>): boolean => !!get(response ?? {}, key) ?? false
    )

export const getVersionsConfig = (): Promise<JSONResponse<VersionsConfigDTO>> =>
    getConfig<VersionsConfigDTO>(VERSIONS_FILE_NAME)

export const getBroadworksSSOConfig = (): Promise<JSONResponse<BroadworksSSOConfigDTO>> =>
    getConfig<BroadworksSSOConfigDTO>(BROADWORKS_SSO_FILE_NAME)

export const getMicrosoftSSOConfig = (): Promise<JSONResponse<MicrosoftSSOConfigDTO>> =>
    getConfig<MicrosoftSSOConfigDTO>(MICROSOFT_SSO_FILE_NAME)

export const getMiscAppConfig = (): Promise<JSONResponse<MiscAppConfigDTO>> =>
    getConfig<MiscAppConfigDTO>(MISC_APP_CONFIG_FILE_NAME)

export const getTelsysMoalConfig = (): Promise<JSONResponse<TelSysModalConfigDTO>> =>
    getConfig<TelSysModalConfigDTO>(TELSYS_MODAL_CONFNIG_NAME)

/**
 * Loads the configured new UI target URL from Generic Config.
 *
 * @returns The parsed new UI config, or null when the config is unavailable.
 */
export const getNewUIConfig = (): Promise<JSONResponse<NewUIConfigDTO>> => getConfig<NewUIConfigDTO>(NEW_UI_FILE_NAME)

/**
 * Loads the CME capabilities that are not yet available per TelSys type.
 *
 * @returns The parsed config; rejects when the config file is missing.
 */
export const getCMEDisabledCapabilitiesConfig = (): Promise<JSONResponse<CMEDisabledCapabilitiesConfigDTO>> =>
    getConfig<CMEDisabledCapabilitiesConfigDTO>(CME_DISABLED_CAPABILITIES_FILE_NAME)
